import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

function FileUpload({ invoiceId, createdBy, onAttachmentsChange, status, isCreateMode, onFilesSelected }) {
    const { user, hasRole } = useAuth();
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    // Check if user can upload (admin, IE, or invoice creator)
    const canUpload = isCreateMode || ((hasRole('admin') ||
        hasRole('invoice-editor') ||
        (createdBy && user && createdBy.userId === user.id)) && status !== 'completed');
    // Load existing attachments when component mounts
    useEffect(() => {
        if (invoiceId) {
            loadAttachments();
        }
    }, [invoiceId]);

    const loadAttachments = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`http://localhost:5000/api/attachments/invoice/${invoiceId}`);
            setUploadedFiles(response.data || []);
            if (onAttachmentsChange) {
                onAttachmentsChange(response.data?.length || 0);
            }
        } catch (error) {
            console.error('Error loading attachments:', error);
        } finally {
            setLoading(false);
        }
    };

    const MAX_SIZE = 20 * 1024 * 1024; // 20 MB

    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const oversized = files.filter(f => f.size > MAX_SIZE);
        if (oversized.length > 0) {
            alert(`The following file(s) exceed the 20 MB limit and were removed:\n${oversized.map(f => f.name).join('\n')}`);
        }
        const valid = files.filter(f => f.size <= MAX_SIZE);
        setSelectedFiles(valid);
        if (isCreateMode && onFilesSelected) {
            onFilesSelected(valid);
        }
    };

    const handleUpload = async () => {
        if (selectedFiles.length === 0) {
            alert('Please select files to upload');
            return;
        }

        if (!invoiceId) {
            alert('Please save the Sales order first before uploading attachments');
            return;
        }

        setUploading(true);

        try {
            // Upload files one by one
            for (const file of selectedFiles) {
                const formData = new FormData();
                formData.append('file', file);
                formData.append('invoiceId', invoiceId);

                await axios.post('http://localhost:5000/api/attachments/upload', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
            }

            setSelectedFiles([]);
            // Reload attachments to show new files
            await loadAttachments();

            alert(`${selectedFiles.length} file(s) uploaded successfully!`);
        } catch (error) {
            console.error('Upload error:', error);
            alert(error.response?.data?.error || 'Failed to upload files');
        } finally {
            setUploading(false);
        }
    };

    const handleDownload = async (file) => {
        try {
            // Get a short-lived presigned S3 URL from the server
            const response = await axios.get(
                `http://localhost:5000/api/attachments/${file.id}/download`
            );
            // Open directly in browser – S3 presigned URL handles the download
            const link = document.createElement('a');
            link.href = response.data.url;
            link.setAttribute('download', response.data.filename || file.originalName);
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error('Download error:', err);
            alert('Failed to download file');
        }
    };

    const formatFileSize = (bytes) => {
        if (!bytes) return '0 B';
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
        return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
    };

    return (
        <div style={{ marginTop: '1.5rem' }}>
            <h3 style={{ marginBottom: '1rem', color: '#667eea' }}>📎 Attachments</h3>

            {/* Upload Section - Only show for authorized users */}
            {canUpload && (
                <div style={{
                    border: '2px dashed #d1d5db',
                    borderRadius: '8px',
                    padding: '1.5rem',
                    textAlign: 'center',
                    background: '#f9fafb',
                    marginBottom: '1rem'
                }}>
                    <input
                        type="file"
                        multiple
                        onChange={handleFileSelect}
                        accept=".pdf,.png,.jpg,.jpeg,.doc,.docx"
                        style={{
                            display: 'block',
                            margin: '0 auto 1rem auto',
                            padding: '0.5rem',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            background: 'white'
                        }}
                    />

                    <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '0.5rem' }}>
                        Supported: PDF, PNG, JPG, DOC, DOCX (Max 20 MB per file)
                    </p>

                    {selectedFiles.length > 0 && (
                        <div style={{ marginTop: '1rem' }}>
                            <p style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                                {selectedFiles.length} file(s) selected:
                            </p>
                            <ul style={{ listStyle: 'none', padding: 0, textAlign: 'left', maxWidth: '500px', margin: '0 auto' }}>
                                {selectedFiles.map((file, index) => (
                                    <li key={index} style={{
                                        padding: '0.5rem',
                                        background: 'white',
                                        marginBottom: '0.5rem',
                                        borderRadius: '4px',
                                        fontSize: '0.9rem'
                                    }}>
                                        📄 {file.name} ({formatFileSize(file.size)})
                                    </li>
                                ))}
                            </ul>

                            <button
                                type="button"
                                onClick={handleUpload}
                                disabled={uploading || (!invoiceId && !isCreateMode)}
                                style={{
                                    marginTop: '1rem',
                                    padding: '0.75rem 2rem',
                                    background: uploading ? '#9ca3af' : '#10b981',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: uploading || (!invoiceId && !isCreateMode) ? 'not-allowed' : 'pointer',
                                    fontWeight: '600',
                                    fontSize: '1rem',
                                    display: isCreateMode ? 'none' : 'block' // Hide upload button in create mode
                                }}
                            >
                                {uploading ? 'Uploading...' : invoiceId ? 'Upload Files' : 'Save Sales Order First'}
                            </button>
                            {isCreateMode && (
                                <div style={{ marginTop: '1rem', color: '#059669', fontStyle: 'italic', fontWeight: '500' }}>
                                    ✅ Files selected. They will be uploaded automatically when you click "Draft Sales Order".
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Permission message for non-uploaders */}
            {!canUpload && status != 'completed' && (
                <div style={{
                    padding: '1rem',
                    background: '#fef3c7',
                    borderRadius: '8px',
                    border: '1px solid #fbbf24',
                    color: '#92400e',
                    textAlign: 'center',
                    marginBottom: '1rem',
                    fontSize: '0.9rem'
                }}>
                    ⚠️ Only Admin, Invoice Editor, or the invoice creator can upload attachments.
                </div>
            )}

            {/* Loading indicator */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '1rem', color: '#6b7280' }}>
                    Loading attachments...
                </div>
            )}

            {/* Display existing attachments - visible to all authorized users */}
            {!loading && uploadedFiles.length > 0 && (
                <div style={{ marginTop: '1rem' }}>
                    <h4 style={{ marginBottom: '0.75rem', color: '#1e40af' }}>
                        📁 Uploaded Files ({uploadedFiles.length}):
                    </h4>
                    <ul style={{ listStyle: 'none', padding: 0 }}>
                        {uploadedFiles.map((file) => (
                            <li key={file.id} style={{
                                padding: '0.75rem 1rem',
                                background: '#eff6ff',
                                borderRadius: '8px',
                                marginBottom: '0.5rem',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                border: '1px solid #dbeafe'
                            }}>
                                <div>
                                    <div style={{ fontWeight: '600', color: '#1e40af' }}>
                                        📄 {file.originalName}
                                    </div>
                                    <div style={{ fontSize: '0.85rem', color: '#6b7280', marginTop: '0.25rem' }}>
                                        {formatFileSize(file.size)} •
                                        Uploaded by {file.uploadedBy?.name || 'Unknown'} •
                                        {(file.createdAtIST || file.uploadedAtIST) || new Date(file.createdAt || file.uploadedAt).toLocaleDateString()}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleDownload(file)}
                                    style={{
                                        padding: '0.5rem 1rem',
                                        background: '#3b82f6',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontWeight: '600',
                                        fontSize: '0.875rem'
                                    }}
                                >
                                    ⬇ Download
                                </button>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {/* No attachments message */}
            {!loading && uploadedFiles.length === 0 && invoiceId && (
                <div style={{
                    textAlign: 'center',
                    padding: '1rem',
                    color: '#9ca3af',
                    fontStyle: 'italic'
                }}>
                    No attachments uploaded yet.
                </div>
            )}
        </div>
    );
}

export default FileUpload;
